import { BeneficiaryVisibilityType } from "@/modules/beneficiary/common/enum";
import { BeneficiaryLogo } from "@/modules/beneficiary/components";
import { useGetBeneficiariesQuery } from "@/modules/beneficiary/services";
import { INITIAL_PAGE } from "@/shared/common/constants";
import { AppPaths, CloudflareAsset } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, EmptyOrErrorContent, SeeMore } from "@/shared/components";
import { getErrorMessage } from "@/shared/helpers";
import { useAppDispatch } from "@/shared/hooks";
import { setActiveBeneficiaryId, setBeneficiaryActiveTab } from "@/shared/state/common";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

export const BeneficiariesWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const onShowDetails = (beneficiaryId: string) => {
    dispatch(setBeneficiaryActiveTab(BeneficiaryVisibilityType.Private));
    dispatch(setActiveBeneficiaryId(beneficiaryId));
    if (!location.pathname.includes(AppPaths.Beneficiaries)) {
      navigate(AppPaths.Beneficiaries);
    }
  };
  const {
    data: beneficiaries,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetBeneficiariesQuery(
    {
      type: BeneficiaryVisibilityType.Private,
      pageIndex: INITIAL_PAGE,
      pageSize: 5,
    },
    { refetchOnMountOrArgChange: true }
  );

  const handleSeeMore = () => {
    navigate(AppPaths.Beneficiaries);
  };

  return (
    <div className="dashboard-beneficiaries-widget">
      <div className="widget beneficiaries-widget">
        <div>
          <div
            className="full-width flexbox align-items--center justify-content--between pb-20">
            <Text className="truncate" weight="bold" size="large">
              {t("beneficiary.title")}
            </Text>
            <SeeMore action={handleSeeMore} className="ml-8"/>
          </div>
          {!isError && !beneficiaries?.length && !isFetching ? (
            <div className="full-width flexbox align-items--center pt-64 pb-64 mt-6 mb-6">
              <EmptyOrErrorContent
                buttonIcon={(props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.AddLight}/>
                )}
                buttonType="primary"
                buttonTitle={t("beneficiary.createNew")}
                title={t("beneficiary.noBeneficiariesTitle")}
                subtitle={t("beneficiary.noBeneficiariesSubtitle")}
                action={() => navigate(AppPaths.CreateBeneficiary)}
              />
            </div>
          ) : isError && !isFetching ? (
            <div className="full-width flexbox align-items--center pt-64 pb-64 mt-6 mb-6">
              <EmptyOrErrorContent
                isError={true}
                title={t(getErrorMessage(error as IError))}
                action={refetch}
              />
            </div>
          ) : (
            isFetching ? (
              <div
                className="full-width flexbox justify-content--center align-items--center loader-wrapper">
                <Spinner isFullScreen={false} size="small"/>
              </div>
            ) : (
              <div className="beneficiaries-grid">
                {beneficiaries?.map((beneficiary) => (
                  <div
                    key={beneficiary.id}
                    className="beneficiaries-grid-item cursor-pointer"
                    onClick={() => onShowDetails(beneficiary.id)}
                  >
                    <BeneficiaryLogo name={beneficiary.name}/>
                    <Text size="small" className="beneficiary-widget-name">
                      {beneficiary?.name}
                    </Text>
                  </div>
                ))}
                {!!beneficiaries?.length && (
                  <div className="beneficiaries-grid-item">
                    <Button
                      className="attachment-button"
                      type="tertiary"
                      onClick={() => navigate(AppPaths.CreateBeneficiary)}
                      iconProps={{
                        Component: (props) => (
                          <CloudFlareImage
                            {...props}
                            type="quaternary"
                            size="large"
                            path={CloudflareAsset.AddGrey}
                          />
                        )
                      }}
                    />
                    <Text size="small" className="beneficiary-widget-name">
                      {t("beneficiary.createNew")}
                    </Text>
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};
